import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { 
  Truck, 
  Search, 
  Navigation, 
  Phone, 
  Mail, 
  MapPin, 
  Star,
  Eye,
  Edit,
  MoreVertical,
  Clock,
  DollarSign,
  Trash2,
  CheckCircle,
  XCircle,
  Route,
  Users
} from "lucide-react";
import { 
  getAllDrivers, 
  getDriversByStatus, 
  updateDriverStatus, 
  approveRider, 
  rejectRider, 
  deleteRider, 
  getRiderStats,
  assignRoute,
  unassignRoute
} from "../../api/pharmacyService";
import { toast } from "sonner";
import GlobalLoader from "../../components/ui/GlobalLoader";

interface Driver {
  id?: string;
  _id?: string; // MongoDB ObjectId
  name: string;
  email: string;
  phone: string;
  status: 'online' | 'offline' | 'on-delivery' | 'break';
  accountStatus?: 'verified' | 'rejected' | 'suspended' | 'pending';
  currentLocation?: {
    lat: number;
    lng: number;
    address: string;
  };
  rating: number;
  totalDeliveries: number;
  completedToday: number;
  joinedDate: string;
  isActive: boolean;
  isVerified: boolean;
  currentRoute?: string;
  earnings: {
    today: number;
    week: number;
    month: number;
  };
}

interface RouteData {
  routeName: string;
  pickupLocation: string;
  deliveryLocation: string;
}

export function DriversPage() {
  const [drivers, setDrivers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [accountStatusFilter, setAccountStatusFilter] = useState("all");
  const [routeStatusFilter, setRouteStatusFilter] = useState("all");
  const [currentPageAccounts, setCurrentPageAccounts] = useState(1);
  const [currentPageRoutes, setCurrentPageRoutes] = useState(1);
  const [itemsPerPage] = useState(10);
  const [riderStats, setRiderStats] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("accounts");
  
  // Route assignment dialog state
  const [isRouteDialogOpen, setIsRouteDialogOpen] = useState(false);
  const [selectedRider, setSelectedRider] = useState<Driver | null>(null);
  const [routeData, setRouteData] = useState<RouteData>({
    routeName: "",
    pickupLocation: "",
    deliveryLocation: ""
  });

  // Helper function to get the correct ID
  const getDriverId = (driver: Driver): string | null => {
    if (driver.id) return driver.id;
    if (driver._id) return driver._id;
    console.error('Driver missing ID:', driver);
    return null;
  };

  // Fetch drivers and stats
  const fetchDrivers = async () => {
    try {
      setLoading(true);
      console.log('DriversPage: Fetching riders from API...');
      const [driversData, statsData] = await Promise.all([
        getAllDrivers(),
        getRiderStats()
      ]);
      console.log('DriversPage: API response:', driversData);
      console.log('DriversPage: Stats response:', statsData);
      
      setDrivers(driversData);
      setRiderStats(statsData);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      toast.error('Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  // Filter drivers for Account Management tab
  const filteredAccountDrivers = drivers.filter(driver => {
    const matchesSearch = 
      driver.email.toLowerCase().includes(searchTerm.toLowerCase()) || 
      driver.name.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesStatus = true;
    if (accountStatusFilter === "pending") {
      matchesStatus = !driver.isVerified;
    } else if (accountStatusFilter === "verified") {
      matchesStatus = driver.isVerified === true;
    } 
    else if (accountStatusFilter === "rejected") {
      matchesStatus = driver.accountStatus === 'rejected';
    }
    
    return matchesSearch && matchesStatus;
  });

  // Filter drivers for Route Assignment tab
  const filteredRouteDrivers = drivers.filter(driver => {
    const matchesSearch = 
      driver.email.toLowerCase().includes(searchTerm.toLowerCase()) || 
      driver.name.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesStatus = true;
    if (routeStatusFilter === "available") {
      matchesStatus = driver.status === 'online' && !driver.currentRoute;
    } else if (routeStatusFilter === "on-delivery") {
      matchesStatus = driver.status === 'on-delivery';
    } else if (routeStatusFilter === "assigned") {
      matchesStatus = !!driver.currentRoute;
    } else if (routeStatusFilter === "offline") {
      matchesStatus = driver.status === 'offline';
    }
    
    return matchesSearch && matchesStatus && driver.isVerified;
  });

  // Pagination for Account Management
  const totalPagesAccounts = Math.ceil(filteredAccountDrivers.length / itemsPerPage);
  const startIndexAccounts = (currentPageAccounts - 1) * itemsPerPage;
  const endIndexAccounts = startIndexAccounts + itemsPerPage;
  const paginatedAccountDrivers = filteredAccountDrivers.slice(startIndexAccounts, endIndexAccounts);

  // Pagination for Route Assignment
  const totalPagesRoutes = Math.ceil(filteredRouteDrivers.length / itemsPerPage);
  const startIndexRoutes = (currentPageRoutes - 1) * itemsPerPage;
  const endIndexRoutes = startIndexRoutes + itemsPerPage;
  const paginatedRouteDrivers = filteredRouteDrivers.slice(startIndexRoutes, endIndexRoutes);

  // Approve rider
  const approveRiderHandler = async (driver: Driver) => {
    const riderId = getDriverId(driver);
    if (!riderId) {
      toast.error('Invalid rider ID');
      return;
    }

    try {
      const success = await approveRider(riderId);
      if (success) {
        setDrivers(prev => 
          prev.map(d => 
            getDriverId(d) === riderId 
              ? { ...d, isActive: true, status: 'online', isVerified: true, accountStatus: 'verified' }
              : d
          )
        );
        toast.success('Rider approved successfully');
      } else {
        toast.error('Failed to approve rider');
      }
    } catch (error) {
      console.error('Error approving rider:', error);
      toast.error('Failed to approve rider');
    }
  };

  // Reject rider
  const rejectRiderHandler = async (driver: Driver, notes?: string) => {
    const riderId = getDriverId(driver);
    if (!riderId) {
      toast.error('Invalid rider ID');
      return;
    }

    try {
      const success = await rejectRider(riderId, notes);
      if (success) {
        setDrivers(prev => 
          prev.map(d => 
            getDriverId(d) === riderId 
              ? { ...d, isActive: false, status: 'offline', isVerified: false, accountStatus: 'rejected' }
              : d
          )
        );
        // toast.success('Rider rejected successfully');
      } else {
        toast.error('Failed to reject rider');
      }
    } catch (error) {
      console.error('Error rejecting rider:', error);
      toast.error('Failed to reject rider');
    }
  };

  // Delete rider
  const deleteRiderHandler = async (driver: Driver) => {
    const riderId = getDriverId(driver);
    if (!riderId) {
      toast.error('Invalid rider ID');
      return;
    }

    try {
      const success = await deleteRider(riderId);
      if (success) {
        setDrivers(prev => prev.filter(d => getDriverId(d) !== riderId));
        toast.success('Rider deleted successfully');
      } else {
        toast.error('Failed to delete rider');
      }
    } catch (error) {
      console.error('Error deleting rider:', error);
      toast.error('Failed to delete rider');
    }
  };

  // Open route assignment dialog
  const openRouteDialog = (rider: Driver) => {
    setSelectedRider(rider);
    setRouteData({
      routeName: rider.currentRoute || "",
      pickupLocation: "",
      deliveryLocation: ""
    });
    setIsRouteDialogOpen(true);
  };

  // Assign route to rider
  const handleAssignRoute = async () => {
    if (!selectedRider) return;
    
    const riderId = getDriverId(selectedRider);
    if (!riderId) {
      toast.error('Invalid rider ID');
      return;
    }

    if (!routeData.routeName || !routeData.pickupLocation || !routeData.deliveryLocation) {
      toast.error('Please fill all route details');
      return;
    }

    try {
      const success = await assignRoute(riderId, routeData);
      if (success) {
        setDrivers(prev => 
          prev.map(d => 
            getDriverId(d) === riderId 
              ? { ...d, currentRoute: routeData.routeName, status: 'on-delivery' }
              : d
          )
        );
        toast.success('Route assigned successfully');
        setIsRouteDialogOpen(false);
        setSelectedRider(null);
      } else {
        toast.error('Failed to assign route');
      }
    } catch (error) {
      console.error('Error assigning route:', error);
      toast.error('Failed to assign route');
    }
  };

  // Unassign route from rider
  const handleUnassignRoute = async (driver: Driver) => {
    const riderId = getDriverId(driver);
    if (!riderId) {
      toast.error('Invalid rider ID');
      return;
    }

    try {
      const success = await unassignRoute(riderId);
      if (success) {
        setDrivers(prev => 
          prev.map(d => 
            getDriverId(d) === riderId 
              ? { ...d, currentRoute: undefined, status: 'online' }
              : d
          )
        );
        // toast.success('Route unassigned successfully');
      } else {
        toast.error('Failed to unassign route');
      }
    } catch (error) {
      console.error('Error unassigning route:', error);
      toast.error('Failed to unassign route');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'bg-green-100 text-green-800';
      case 'on-delivery':
        return 'bg-blue-100 text-blue-800';
      case 'break':
        return 'bg-yellow-100 text-yellow-800';
      case 'offline':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAccountStatusBadge = (driver: Driver) => {
    if (!driver.isVerified) {
      return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
    } else if (driver.accountStatus === 'rejected') {
      return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
    } else if (driver.isVerified) {
      return <Badge className="bg-green-100 text-green-800">Verified</Badge>;
    }
    return null;
  };

  if (loading) {
    return (
      <GlobalLoader 
        isLoading={true} 
        message="Processing please wait..." 
        subMessage="Please wait" 
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          {/* <h1 className="text-3xl font-semibold text-gray-900">Rider Management</h1> */}
          <p className="text-gray-600 mt-1">Manage and monitor delivery riders</p>
          {riderStats && (
            <div className="flex gap-4 mt-2 text-sm text-gray-500">
              <span>Total: {riderStats.totalRiders || 0}</span>
              <span>Verified: {riderStats.verifiedRiders || 0}</span>
              <span>Pending: {riderStats.pendingRiders || 0}</span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          {/* <TabsTrigger value="routes" className="flex items-center gap-2">
            <Route className="w-4 h-4" />
            Route Assignment
          </TabsTrigger> */}
          <TabsTrigger value="accounts" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Account Management
          </TabsTrigger>
        </TabsList>

        {/* Account Management Tab */}
        <TabsContent value="accounts" className="space-y-4">
          {/* Filters */}
    <Card>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search riders..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-11"
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <Select value={accountStatusFilter} onValueChange={setAccountStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                      {/* <SelectItem value="rejected">Rejected</SelectItem> */}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Riders List */}
          <div className="space-y-4">
            {paginatedAccountDrivers.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No riders found</p>
                </CardContent>
              </Card>
            ) : (
              paginatedAccountDrivers.map((driver, index) => (
                <Card key={driver.id || driver._id || index} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Truck className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">
                            {driver.name || 'Unknown Driver'}
                          </h3>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <div className="flex items-center space-x-1">
                              <Phone className="w-4 h-4" />
                              <span>{driver.phone || 'N/A'}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Mail className="w-4 h-4" />
                              <span>{driver.email || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <div>
                          {getAccountStatusBadge(driver)}
                        </div>
                        {!driver.isVerified && driver.accountStatus !== 'rejected' ? (
                          <div className="flex space-x-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => approveRiderHandler(driver)}
                              className="text-green-600 hover:text-green-700 border-green-600"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => rejectRiderHandler(driver)}
                              className="text-red-600 hover:text-red-700 border-red-600"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <div className="flex space-x-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => deleteRiderHandler(driver)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
                  </div>

          {/* Pagination */}
          {totalPagesAccounts > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {startIndexAccounts + 1} to {Math.min(endIndexAccounts, filteredAccountDrivers.length)} of {filteredAccountDrivers.length} riders
                  </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPageAccounts(prev => Math.max(prev - 1, 1))}
                  disabled={currentPageAccounts === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {currentPageAccounts} of {totalPagesAccounts}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPageAccounts(prev => Math.min(prev + 1, totalPagesAccounts))}
                  disabled={currentPageAccounts === totalPagesAccounts}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Route Assignment Tab */}
        {/* <TabsContent value="routes" className="space-y-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search riders..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-11"
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <Select value={routeStatusFilter} onValueChange={setRouteStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="on-delivery">On Delivery</SelectItem>
                      <SelectItem value="assigned">Assigned</SelectItem>
                      <SelectItem value="offline">Offline</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            {paginatedRouteDrivers.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Route className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No verified riders found</p>
                </CardContent>
              </Card>
            ) : (
              paginatedRouteDrivers.map((driver, index) => (
                <Card key={driver.id || driver._id || index} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Truck className="w-6 h-6 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">
                            {driver.name || 'Unknown Driver'}
                          </h3>
                          <div className="flex items-center flex-wrap gap-4 text-sm text-gray-500 mt-1">
                            <div className="flex items-center space-x-1">
                              <Phone className="w-4 h-4" />
                              <span>{driver.phone || 'N/A'}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Mail className="w-4 h-4" />
                              <span>{driver.email || 'N/A'}</span>
                            </div>
                            {driver.currentRoute && (
                              <div className="flex items-center space-x-1">
                                <MapPin className="w-4 h-4" />
                                <span className="font-medium">{driver.currentRoute}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                         <Badge className={getStatusColor(driver.status)}>
                          {driver.status.replace("-", " ")}
                        </Badge> 
                        {driver.currentRoute ? (
                          <div className="flex space-x-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openRouteDialog(driver)}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit Route
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleUnassignRoute(driver)}
                              className="text-orange-600 hover:text-orange-700"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Unassign
                            </Button>
                          </div>
                        ) : (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openRouteDialog(driver)}
                            // disabled={driver.status === 'offline'}
                          >
                            <Navigation className="w-4 h-4 mr-1" />
                            Assign Route
                          </Button>
                        )}
                      </div>
        </div>
      </CardContent>
    </Card>
              ))
            )}
          </div>

          {totalPagesRoutes > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {startIndexRoutes + 1} to {Math.min(endIndexRoutes, filteredRouteDrivers.length)} of {filteredRouteDrivers.length} riders
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPageRoutes(prev => Math.max(prev - 1, 1))}
                  disabled={currentPageRoutes === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {currentPageRoutes} of {totalPagesRoutes}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPageRoutes(prev => Math.min(prev + 1, totalPagesRoutes))}
                  disabled={currentPageRoutes === totalPagesRoutes}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </TabsContent> */}
        
      </Tabs>

      {/* Route Assignment Dialog */}
      <Dialog open={isRouteDialogOpen} onOpenChange={setIsRouteDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-white">
          <DialogHeader>
            <DialogTitle>Assign Route to Rider</DialogTitle>
            <DialogDescription>
              Assign a delivery route to {selectedRider?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="routeName">Route Name</Label>
              <Input
                id="routeName"
                placeholder="e.g., Downtown Route A"
                value={routeData.routeName}
                onChange={(e) => setRouteData({ ...routeData, routeName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pickupLocation">Pickup Location</Label>
              <Input
                id="pickupLocation"
                placeholder="e.g., Main Pharmacy, 123 Main St"
                value={routeData.pickupLocation}
                onChange={(e) => setRouteData({ ...routeData, pickupLocation: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="deliveryLocation">Delivery Location</Label>
              <Input
                id="deliveryLocation"
                placeholder="e.g., Customer Address, 456 Oak Ave"
                value={routeData.deliveryLocation}
                onChange={(e) => setRouteData({ ...routeData, deliveryLocation: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRouteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignRoute}>
              Assign Route
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
